import { getSdkConfig, getSdkRelayBaseUrl } from './sdk.js';

export function getConfig() {
  const cfg = getSdkConfig();
  const relayUrl = String(cfg.relayUrl || '').replace(/\/+$/, '');
  const storageUrl = String(cfg.storageUrl || '').replace(/\/+$/, '');
  return {
    ...cfg,
    relayUrl,
    storageUrl,
    relayServers: normalizeServers(relayUrl),
    storageServers: normalizeServers(storageUrl),
  };
}

function normalizeServers(value) {
  const list = Array.isArray(value) ? value : String(value || '').split(',');
  return list.map((item) => String(item || '').trim().replace(/\/+$/, '')).filter(Boolean);
}

export function buildQuery(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    const s = String(value).trim();
    if (s === '') return;
    query.set(key, s);
  });
  return query.toString();
}

export class ExplorerApi {
  constructor(config = getConfig(), relayResolver = getSdkRelayBaseUrl) {
    this.config = config;
    this.relayResolver = relayResolver;
    this.baseUrl = '';
    this.relayServers = config.relayServers || [];
    this.storageServers = config.storageServers || [];
  }

  async get(path, params = {}) {
    const baseUrl = await this.getRelayBaseUrl();
    const query = buildQuery(params);
    const url = `${baseUrl}${path}${query ? `?${query}` : ''}`;
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
    });
    let data = null;
    try {
      data = await response.json();
    } catch (_) {
      data = null;
    }
    if (!response.ok) {
      const detail = normalizeApiError(data, response.status);
      const error = new Error(`${detail.message} (${response.status})`);
      error.status = response.status;
      error.code = detail.code;
      error.url = url;
      throw error;
    }
    return data;
  }

  async getRelayBaseUrl() {
    if (!this.baseUrl) {
      this.baseUrl = await this.relayResolver(this.config);
      this.relayServers = [this.baseUrl];
    }
    return this.baseUrl;
  }

  summary() {
    return this.get('/explorer/summary');
  }

  transactions(params) {
    return this.get('/explorer/transactions', params);
  }

  transactionsDaily(params) {
    return this.get('/explorer/transactions/daily', params);
  }

  transaction(hash) {
    return this.get(`/explorer/transactions/${encodeURIComponent(hash)}`);
  }

  wallets(params) {
    return this.get('/explorer/wallets', params);
  }

  wallet(id) {
    return this.get(`/explorer/wallets/${encodeURIComponent(id)}`);
  }

  dsr(params) {
    return this.get('/explorer/dsr', params);
  }

  dsrItem(id) {
    return this.get(`/explorer/dsr/${encodeURIComponent(id)}`);
  }

  zkCoupons(params) {
    return this.get('/explorer/zk/coupons', params);
  }

  zkCoupon(id) {
    return this.get(`/explorer/zk/coupons/${encodeURIComponent(id)}`);
  }

  zkActiveBlocks(params) {
    return this.get('/explorer/zk/active-blocks', params);
  }

  networkOverview() {
    return this.get('/network/overview');
  }

  networkSystemState() {
    return this.get('/network/system/state');
  }

  networkSettings() {
    return this.get('/network/settings');
  }

  networkLegacySnapshot() {
    return this.get('/network/system/legacy-snapshot');
  }

  networkVotes() {
    return this.get('/network/votes');
  }

  networkNodes() {
    return this.get('/network/nodes');
  }

  networkNodeHeartbeats(params) {
    return this.get('/network/node/heartbeats', params);
  }

  networkServices() {
    return this.get('/network/services');
  }

  networkBlocklist() {
    return this.get('/network/blocklist');
  }

  networkTombstones() {
    return this.get('/network/tombstones');
  }

  networkAvailability() {
    return this.get('/network/availability');
  }

  networkAvailabilityEpochs() {
    return this.get('/network/availability/epochs');
  }

  networkRewardEpochs() {
    return this.get('/network/rewards/epochs');
  }

  networkStorageLeaseBillingEpochs(params) {
    return this.get('/network/storage-lease/billing/epochs', params);
  }

  networkStorageLeaseBillingCharges(params) {
    return this.get('/network/storage-lease/billing/charges', params);
  }

  async pingServers() {
    const baseUrl = await this.getRelayBaseUrl();
    const relays = await Promise.all([baseUrl, ...this.relayServers.filter((url) => url !== baseUrl)].map((url) => pingHttp(`${url}/explorer/summary`)));
    const storages = await Promise.all(this.storageServers.map((url) => pingStorage(url)));
    return { relays, storages };
  }
}

function normalizeApiError(data, status) {
  if (data && typeof data === 'object') {
    const code = data.code || data.error_code || data.errorCode || '';
    const message = data.error || data.message || data.detail || code || `HTTP ${status}`;
    return { code, message };
  }
  return { code: '', message: `HTTP ${status}` };
}

async function pingHttp(url) {
  const started = performance.now();
  try {
    const response = await fetchWithTimeout(url, { headers: { Accept: 'application/json' } }, 3500);
    return { url, ok: response.ok, rtt: Math.round(performance.now() - started) };
  } catch (error) {
    return { url, ok: false, rtt: null, error: error?.message || String(error) };
  }
}

async function pingStorage(url) {
  const wsUrl = toWebSocketUrl(url);
  if (!wsUrl || typeof WebSocket === 'undefined') {
    return pingHttp(url);
  }

  const started = performance.now();
  return new Promise((resolve) => {
    let settled = false;
    let socket = null;
    const finish = (ok, error = '') => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try { socket?.close(); } catch (_) {}
      resolve({ url, ok, rtt: ok ? Math.round(performance.now() - started) : null, error });
    };
    const timer = setTimeout(() => finish(false, 'тайм-аут'), 3500);
    try {
      socket = new WebSocket(wsUrl);
    } catch (error) {
      finish(false, error?.message || String(error));
      return;
    }
    socket.addEventListener('open', () => socket.send(JSON.stringify({ action: 'ping' })));
    socket.addEventListener('message', (event) => {
      try {
        const data = JSON.parse(String(event.data || '{}'));
        finish(data?.action === 'pong' || data?.status === 'ok', '');
      } catch (_) {
        finish(false, 'некорректный ответ');
      }
    });
    socket.addEventListener('error', () => finish(false, 'ошибка подключения'));
    socket.addEventListener('close', () => finish(false, 'соединение закрыто'));
  });
}

function toWebSocketUrl(url) {
  const s = String(url || '').trim();
  if (!s) return '';
  if (/^wss?:\/\//i.test(s)) return s;
  if (/^https?:\/\//i.test(s)) return s.replace(/^http/i, 'ws');
  return '';
}

async function fetchWithTimeout(url, options, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}
